// THE PIECES THE QUESTIONNAIRE AND THE CONSENT DRAW, AT MODULE SCOPE (TW-0504, 2026-10-29).
//
// Every one of these was declared inside Form until this card (docs/FORM-AUDIT-2026-10-26.md § 10). A component declared in
// a render is a NEW component on every render, so React threw away every box these drew and drew it again on every change
// anywhere on the page: one keystroke in the tattoo description drew 25 of them again on a South Carolina page, a screen
// reader lost its place after each answer, a box just ticked lost the focus (it was a different box), and the aftercare sheet
// a client had opened to read folded shut at the next keystroke.
//
// Here each piece is memoized on its own props: what it shows (its answer or its tick, its words, the refusal's sentence or
// mark while the refusal is about it) and the steady doors it answers through, which Form makes with useStableHandler below.
// A keystroke in a text box changes none of those, so it draws none of these; an answer or a tick draws its own piece and no
// other. THE MARKUP IS THE MARKUP EACH DREW INSIDE FORM, attribute for attribute and word for word: what a client reads, what
// the form posts and what it refuses did not change (tattooAndPiercingFormsAreUnchanged.test.tsx holds the posted body), and
// the handlers each piece calls are Form's own, unchanged. questionnaireKeepsItsPlace.test.tsx holds the rest.
import { memo, useCallback, useLayoutEffect, useRef, type ChangeEvent } from "react";

import ErrorMessage from "./ErrorMessage";
import type { ClauseName, QuestionName } from "./Form";
import { releaseWords } from "./hipaaForm";
import type { Lang, Strings } from "./i18n";
import { ownQuestionInputName, type OwnAnswer } from "./ownQuestions";
import { FIELD_LIMITS, type LimitedField } from "./fieldLimits";
import { sectionsFor, type Notice, type StateField } from "./stateForms";

/** 2026-10-26 — the mark on a box the signature depends on, left unticked while a box refusal is up (Form's BOX_REFUSALS): a
 *  red rule down the box's left edge (the colour is never the only signal: the line under the words says it) */
const MISSED_BOX = " border-l-4 border-red-700 ps-2";
export const MISSED_LINE = "block text-sm text-red-700 font-semibold mt-1";

const CHECKBOX =
  "w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600";

/** THE PROBE: how many times each piece drew, by its name and which one ("QuestionnaireItem:skinConditions"), counted under
 *  test and nowhere else (the production build reads NODE_ENV as "production" and drops the count). The tests read it. */
export const piecesDrawn: Record<string, number> = {};
function drew(piece: string, which = ""): void {
  if (process.env.NODE_ENV === "test") {
    const key = which ? `${piece}:${which}` : piece;
    piecesDrawn[key] = (piecesDrawn[key] ?? 0) + 1;
  }
}

/** A door a memoized piece can hold: the same function on every render, and each call runs the function the page wrote on
 *  its LAST COMMITTED render. That is exactly what a piece ran when every render handed it a fresh handler, so a tick or an
 *  answer does what it did before this card: Form's handlers read the form, the ticks and the refusal as that render saw them,
 *  and still do. Set in a layout effect, which runs at the commit and before any event can reach a box. */
export function useStableHandler<A extends unknown[], R>(fn: (...args: A) => R): (...args: A) => R {
  const latest = useRef(fn);
  useLayoutEffect(() => {
    latest.current = fn;
  });
  return useCallback((...args: A) => latest.current(...args), []);
}

/** One health question: the question, then Yes and No side by side on one line. */
export const QuestionnaireItem = memo(function QuestionnaireItem({
  name,
  label,
  optional = false,
  errorMessage = "",
  note = "",
  value,
  unanswered,
  T,
  isMobile,
  onAnswer,
}: {
  name: QuestionName;
  label: string;
  optional?: boolean;
  errorMessage?: string;
  /** TW-0279 slice 9 — the rule's sentence under the question (a state's own on a cosmetic form: California's herpes
   *  question), drawn only where there is one, so every other question's markup is what it was */
  note?: string;
  /** the answer picked: "yes", "no", or "" before one is */
  value: string;
  /** the refusal's sentence while this is the question left blank (Form's questionRefusedHere), "" otherwise */
  unanswered: string;
  T: Strings;
  isMobile: boolean;
  onAnswer: (e: ChangeEvent<HTMLInputElement>, name: QuestionName) => void;
}) {
  drew("QuestionnaireItem", name);
  // Not required on mobile, because the browser doesn't seem to respect the "required"
  // attribute like say Chrome does.
  // 2026-10-01 — and so handleSubmit refuses a question left blank (healthQuestions.ts), which brings the page here; the
  // sentence is said here too, under the question it is about, until a yes or a no is picked.
  const shown = errorMessage || unanswered;
  return (
    <div className="flex flex-col mb-5">
      <div className="w-full text-xl">{label}</div>
      {note && (
        <p className="text-sm text-gray-600 mt-1" data-cosmetic-why="">
          {note}
        </p>
      )}
      {/* Yes and No side by side on one line — one question, one answer — where they used to
          stack, a line each, on every question of the page. */}
      <div className="w-full flex flex-row gap-x-8 ps-1">
        <label className="inline-flex items-center mt-2">
          <input
            type="radio"
            className="form-radio h-5 w-5 text-gray-600"
            name={name}
            value="yes"
            checked={value === "yes"}
            onChange={(e) => onAnswer(e, name)}
            required={!optional && !isMobile}
          />
          <span className="ml-2 text-gray-700">{T.yes}</span>
        </label>
        <label className="inline-flex items-center mt-2">
          <input
            type="radio"
            className="form-radio h-5 w-5 text-gray-600"
            name={name}
            value="no"
            checked={value === "no"}
            onChange={(e) => onAnswer(e, name)}
            required={!optional && !isMobile}
          />
          <span className="ml-2 text-gray-700">{T.no}</span>
        </label>
      </div>
      {shown && <ErrorMessage message={shown} />}
    </div>
  );
});

/** TW-0412 — one of the shop's own questions, in the owner's words: QuestionnaireItem's markup (the question, then Yes and No
 *  side by side on one line), bound to the own answers, and under the radios the owner's line once the answer picked is the
 *  one it names. Never on a phone's `required`: the submit's gate is the gate, and it brings the page here; its sentence is
 *  said here too, under the question, until a yes or a no is picked. The radios carry the name refusal.ts finds. */
export const OwnQuestionItem = memo(function OwnQuestionItem({
  id,
  text,
  answer,
  note,
  unanswered,
  T,
  isMobile,
  onAnswer,
}: {
  /** the question's id on the shop's row (ownQuestions.ts) */
  id: string;
  text: string;
  answer: string | undefined;
  /** the owner's line under the answer it names (ownQuestionNoteShown), "" otherwise */
  note: string;
  /** the refusal's sentence while this is the question left blank (Form's ownQuestionRefusedHere), "" otherwise */
  unanswered: string;
  T: Strings;
  isMobile: boolean;
  onAnswer: (id: string, value: OwnAnswer) => void;
}) {
  drew("OwnQuestionItem", id);
  const name = ownQuestionInputName({ id });
  return (
    <div className="flex flex-col mb-5" data-own-question={id}>
      <div className="w-full text-xl">{text}</div>
      <div className="w-full flex flex-row gap-x-8 ps-1">
        {(["yes", "no"] as const).map((value) => (
          <label key={value} className="inline-flex items-center mt-2">
            <input
              type="radio"
              className="form-radio h-5 w-5 text-gray-600"
              name={name}
              value={value}
              checked={answer === value}
              onChange={() => onAnswer(id, value)}
              required={!isMobile}
            />
            <span className="ml-2 text-gray-700">{value === "yes" ? T.yes : T.no}</span>
          </label>
        ))}
      </div>
      {/* the owner's line, under the answer it names and never under the other: words the client reads, never a gate */}
      {note && (
        <p className="text-base text-gray-700 mt-3 ps-1" data-own-question-note={id}>
          {note}
        </p>
      )}
      {unanswered && <ErrorMessage message={unanswered} />}
    </div>
  );
});

/** One of the waiver's own clauses, or the terms box, or one of the two optional boxes after it. */
export const AcknowledgmentItem = memo(function AcknowledgmentItem({
  name,
  label,
  optional,
  checked,
  boxRefusalUp,
  T,
  isMobile,
  onTick,
}: {
  name: ClauseName;
  /** the clause's words; the terms box's carry the link, an element Form keeps the same until its words or link move */
  label: string | JSX.Element;
  // TW-0113 — an OPTIONAL box carries no `required` at all, on any browser: the client can sign
  // without it. The same shape QuestionnaireItem above already uses.
  optional?: boolean;
  checked: boolean;
  /** one of the box refusals is the sentence on the page (Form's boxRefusalUp) */
  boxRefusalUp: boolean;
  T: Strings;
  isMobile: boolean;
  onTick: (e: ChangeEvent<HTMLInputElement>, name: ClauseName) => void;
}) {
  drew("AcknowledgmentItem", name);
  // Not required on mobile, because the browser doesn't seem to respect the "required"
  // attribute like say Chrome does.
  // 2026-10-26 — marked while a box refusal is up and this required box is still unticked (boxRefusalUp)
  const missed = !optional && boxRefusalUp && !checked;
  return (
    <div className={`flex items-center mb-4${missed ? MISSED_BOX : ""}`}>
      <span className="me-2 h-100">
        <input
          id={`acknowledgment-and-waiver.${name}`}
          type="checkbox"
          className={CHECKBOX}
          onChange={(e) => {
            onTick(e, name);
          }}
          checked={checked}
          required={!optional && !isMobile}
          aria-invalid={missed || undefined}
        />
      </span>
      <label htmlFor={`acknowledgment-and-waiver.${name}`} className="ml-2 text-base font-medium">
        {label}
        {missed && <span className={MISSED_LINE}>{T.tickThisBox}</span>}
      </label>
    </div>
  );
});

/** TW-0170 — a state's own sentence on the consent that the client ticks (South Carolina's blood-donor
 *  notice): AcknowledgmentItem's markup, bound to the notice ticks rather than the waiver's own clauses. */
export const NoticeItem = memo(function NoticeItem({
  noticeId,
  text,
  ticked,
  boxRefusalUp,
  T,
  isMobile,
  onTick,
}: {
  noticeId: string;
  /** the notice's words in the client's language, the shop's own fields filled (stateForms.ts noticeText) */
  text: string;
  ticked: boolean;
  boxRefusalUp: boolean;
  T: Strings;
  isMobile: boolean;
  onTick: (noticeId: string, checked: boolean) => void;
}) {
  drew("NoticeItem", noticeId);
  const id = `state-notice.${noticeId}`;
  const missed = boxRefusalUp && !ticked;
  return (
    <div className={`flex items-center mb-4${missed ? MISSED_BOX : ""}`}>
      <span className="me-2 h-100">
        <input
          id={id}
          type="checkbox"
          className={CHECKBOX}
          onChange={(e) => onTick(noticeId, e.target.checked)}
          checked={ticked}
          required={!isMobile}
          aria-invalid={missed || undefined}
        />
      </span>
      <label htmlFor={id} className="ml-2 text-base font-medium">
        {text}
        {missed && <span className={MISSED_LINE}>{T.tickThisBox}</span>}
      </label>
    </div>
  );
});

/** 2026-09-24 — one of the shop's own clauses, in the owner's words: AcknowledgmentItem's markup, bound to the
 *  clause ticks. Required like every clause the signature depends on, and never on a phone's `required`. */
export const OwnClauseItem = memo(function OwnClauseItem({
  clauseId,
  text,
  ticked,
  boxRefusalUp,
  T,
  isMobile,
  onTick,
}: {
  clauseId: string;
  text: string;
  ticked: boolean;
  boxRefusalUp: boolean;
  T: Strings;
  isMobile: boolean;
  onTick: (clauseId: string, checked: boolean) => void;
}) {
  drew("OwnClauseItem", clauseId);
  const id = `own-clause.${clauseId}`;
  const missed = boxRefusalUp && !ticked;
  return (
    <div className={`flex items-center mb-4${missed ? MISSED_BOX : ""}`} data-own-clause={clauseId}>
      <span className="me-2 h-100">
        <input
          id={id}
          // 2026-10-02 — named, so a refusal naming this clause takes the person to it (refusal.ts own_clause_unticked)
          name={id}
          type="checkbox"
          className={CHECKBOX}
          onChange={(e) => onTick(clauseId, e.target.checked)}
          checked={ticked}
          required={!isMobile}
          aria-invalid={missed || undefined}
        />
      </span>
      <label htmlFor={id} className="ml-2 text-base font-medium">
        {text}
        {missed && <span className={MISSED_LINE}>{T.tickThisBox}</span>}
      </label>
    </div>
  );
});

/** 2026-10-02 — the card-fee box, cash preferred (Reckless Heart Tattoo): AcknowledgmentItem's markup, bound to its
 *  own tick, in the words the row carries in the client's language. Required like every clause the signature
 *  depends on, and never on a phone's `required`; the block is marked so a refusal takes the person to it. Drawn only
 *  for a shop that turned it on. */
export const CashPreferredItem = memo(function CashPreferredItem({
  settingId,
  sentence,
  ticked,
  boxRefusalUp,
  T,
  isMobile,
  onTick,
}: {
  /** the setting's id on the shop's row (cashPreferred.ts) */
  settingId: string;
  /** its words in the client's language (cashPreferredSentence) */
  sentence: string;
  ticked: boolean;
  boxRefusalUp: boolean;
  T: Strings;
  isMobile: boolean;
  onTick: (checked: boolean) => void;
}) {
  drew("CashPreferredItem", settingId);
  const id = "cash-preferred";
  const missed = boxRefusalUp && !ticked;
  return (
    <div className={`flex items-center mb-4${missed ? MISSED_BOX : ""}`} data-cash-preferred={settingId} data-refusal="cash_preferred_unticked">
      <span className="me-2 h-100">
        <input
          id={id}
          name={id}
          type="checkbox"
          className={CHECKBOX}
          onChange={(e) => onTick(e.target.checked)}
          checked={ticked}
          required={!isMobile}
          aria-invalid={missed || undefined}
        />
      </span>
      <label htmlFor={id} className="ml-2 text-base font-medium">
        {sentence}
        {missed && <span className={MISSED_LINE}>{T.tickThisBox}</span>}
      </label>
    </div>
  );
});

/** 2026-10-08 — A SHOP IN HIPAA MODE: the practice's own Notice of Privacy Practices, before the signature, its title and
 *  its words as the practice wrote them (shown as written in either language: they are the practice's), in a box that
 *  scrolls on a phone, and the ONE box the signature depends on. Marked so a refusal takes the person to it. */
export const PrivacyNoticeItem = memo(function PrivacyNoticeItem({
  title,
  text,
  version,
  ticked,
  T,
  isMobile,
  onTick,
}: {
  title: string;
  text: string;
  /** the notice's fingerprint (hipaaForm.ts privacyNoticeOf) */
  version: string;
  ticked: boolean;
  T: Strings;
  isMobile: boolean;
  onTick: (checked: boolean) => void;
}) {
  drew("PrivacyNoticeItem");
  const id = "privacy-notice";
  return (
    <div className="mb-6" data-privacy-notice={version} data-refusal="privacy_notice_unticked">
      <h3 className="text-lg font-semibold mb-1">{title}</h3>
      <p className="text-base text-gray-600 mb-2">{T.privacyNoticeLead}</p>
      <div className="max-h-64 overflow-y-auto whitespace-pre-wrap border border-gray-300 rounded p-3 mb-3 text-base" tabIndex={0}>
        {text}
      </div>
      <div className="flex items-center">
        <span className="me-2 h-100">
          <input
            id={id}
            name={id}
            type="checkbox"
            className={CHECKBOX}
            onChange={(e) => onTick(e.target.checked)}
            checked={ticked}
            required={!isMobile}
          />
        </span>
        <label htmlFor={id} className="ml-2 text-base font-medium">
          {T.privacyNoticeAck}
        </label>
      </div>
    </div>
  );
});

/** 2026-10-08 — A SHOP IN HIPAA MODE WITH ITS PHOTO RELEASE ON: a HIPAA authorization, its own section with its own heading,
 *  every element 45 CFR 164.508(c) asks for in the client's language, and one box the client may leave alone. Never
 *  required: a client who does not tick it signs the waiver all the same, and the API records that it was not signed. */
export const PhotoReleaseItem = memo(function PhotoReleaseItem({
  shopName,
  years,
  signed,
  T,
  onTick,
}: {
  shopName: string;
  years: number;
  signed: boolean;
  T: Strings;
  onTick: (checked: boolean) => void;
}) {
  drew("PhotoReleaseItem");
  const id = "photo-release";
  const words = releaseWords(T, shopName, years);
  return (
    <div className="mb-6 p-3 border border-gray-300 rounded" data-photo-release={years}>
      <h3 className="text-lg font-semibold mb-2">{words.heading}</h3>
      <ul className="list-disc ml-5 mb-3 text-base">
        {words.lines.map((line) => (
          <li key={line} className="mb-1">
            {line}
          </li>
        ))}
      </ul>
      <div className="flex items-center">
        <span className="me-2 h-100">
          <input
            id={id}
            name={id}
            type="checkbox"
            className={CHECKBOX}
            onChange={(e) => onTick(e.target.checked)}
            checked={signed}
          />
        </span>
        <label htmlFor={id} className="ml-2 text-base font-medium">
          {words.box}
        </label>
      </div>
    </div>
  );
});

/** TW-0173 — a notice that carries a whole sheet: South Carolina's aftercare suggestions, which its statute says
 *  the consent "must contain, at a minimum" (S.C. Code Ann. § 44-34-60(B)(1)). Folded under one line that names
 *  the client's own site as they typed it above, so a phone's consent stays readable; opened, it is the aftercare
 *  sheet word for word, and the signed waiver prints it whole. Not a box: the client signs the consent it is in.
 *  TW-0504 — WHETHER IT IS OPEN IS FORM'S OWN STATE (`open`, by the notice's id), and the client's tap on its line is the
 *  only thing that changes it: until this card the <details> held it alone, inside a piece made anew on every render, so the
 *  next keystroke anywhere folded it shut mid-read. */
export const NoticeSheet = memo(function NoticeSheet({
  notice,
  lang,
  line,
  site,
  procedures,
  placement,
  open,
  onToggle,
}: {
  notice: Notice;
  lang: Lang;
  /** the line it is folded under, in the client's language (stateForms.ts noticeText) */
  line: string;
  /** TW-0279 — a cosmetic tattoo's site is its procedures (its placement box holds only the side), the line the signed
   *  waiver prints: "Aftercare suggestions for your cosmetic tattoo (Lips: lip liner)"; TW-0526 — a piercing's is the
   *  placement it picked, "Aftercare suggestions for your piercing (Earlobe)" (Form's sheetSite) */
  site: string;
  /** the cosmetic procedures this signing named (Form's `procedures`), and the placement it posts */
  procedures: readonly string[];
  placement: string;
  open: boolean;
  onToggle: (noticeId: string, open: boolean) => void;
}) {
  drew("NoticeSheet", notice.id);
  const es = lang === "es";
  const intro = es ? notice.intro_es : notice.intro_en;
  // TW-0279 slice 6 — a section kept for some cosmetic procedures is shown only to a signing that named one of them, so
  // the consent's aftercare is the client's own (New Jersey's "specific to the procedure performed"); the API prints the
  // same sections on the signed waiver. TW-0526 — and the piercing sheet's mouth section only for a piercing in the mouth
  const sections = sectionsFor(notice, lang, procedures, placement);
  return (
    <details
      className="mb-4 rounded border border-gray-300 px-3 py-2"
      data-state-notice={notice.id}
      open={open}
      onToggle={(e) => onToggle(notice.id, e.currentTarget.open)}
    >
      <summary className="text-base font-medium cursor-pointer">{site ? `${line} (${site})` : line}</summary>
      <div className="mt-2 text-sm">
        {intro ? <p className="mb-2">{intro}</p> : null}
        {sections.map((section) => (
          <div key={section.title} className="mb-2">
            <p className="font-semibold">{section.title}</p>
            {section.lead ? <p>{section.lead}</p> : null}
            <ul className="list-disc pl-5">
              {section.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            {section.tail ? <p>{section.tail}</p> : null}
          </div>
        ))}
      </div>
    </details>
  );
});

/** TW-0526, the client-form side (2026-10-29) — ONE BOX A STATE'S RULE NAMES THAT NO YES OR NO CARRIES (stateForms.ts
 *  stateFieldsFor: Oklahoma's "When did you last eat?", Mississippi's "Sex, as it appears on your ID", the parent's phone on every
 *  minor's route), drawn by its contract entry: the browser's own control for its `input` (a time picker, a list, a phone box),
 *  the line over it in the page's language, `required` where the shop's state names it, so the browser's own stop is first and
 *  TW-0541's mark and line under the box second (`missed`, Form's nativeMissedHere), and the page's or the door's own sentence
 *  under it while that refusal is about this box (`refusedLine`). A box a state asks of everyone it reaches and requires
 *  nowhere here says so beside its line (`optionalWord`). The typed box takes its one length, the root form-field-limits.json's;
 *  a time picker and a list take none (the door keeps their answer in its shape). Its block is marked for its refusal by the box's
 *  key (refusal.ts BLOCK_BY_DETAIL). */
export const StateFieldBox = memo(function StateFieldBox({
  field,
  lang,
  value,
  chooseWord,
  optionalWord,
  missed,
  missedLine,
  refusedLine,
  onChange,
}: {
  field: StateField;
  lang: Lang;
  value: string;
  /** the list's first, empty choice ("Choose one") */
  chooseWord: string;
  /** "optional", beside the line of a box asked here and required nowhere the shop's state names it */
  optionalWord: string;
  missed: boolean;
  missedLine: string;
  refusedLine: string;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}) {
  drew("StateFieldBox", field.key);
  const id = `state-field-${field.key}`;
  const words = lang === "es" ? field.es : field.en;
  return (
    <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0" data-refusal={`state_field.${field.key}`}>
      <label className="w-full sm:w-1/3 text-xl" htmlFor={id}>
        {words}
        {!field.required && <span className="text-sm text-gray-500"> ({optionalWord})</span>}
      </label>
      <div className="w-full sm:w-2/3">
        {field.input === "select" ? (
          <select
            id={id}
            className="w-full border-2 p-2"
            name={field.key}
            value={value}
            onChange={onChange}
            required={field.required}
            aria-invalid={missed || undefined}
          >
            <option value="">{chooseWord}</option>
            {field.options.map((option) => (
              <option key={option.value} value={option.value}>
                {lang === "es" ? option.es : option.en}
              </option>
            ))}
          </select>
        ) : field.input === "time" ? (
          <input
            id={id}
            className="w-full border-2 p-2"
            type="time"
            name={field.key}
            value={value}
            onChange={onChange}
            required={field.required}
            aria-invalid={missed || undefined}
          />
        ) : (
          <input
            id={id}
            className="w-full border-2 p-2"
            type="tel"
            autoComplete="off"
            name={field.key}
            maxLength={FIELD_LIMITS[field.key as LimitedField]}
            value={value}
            onChange={onChange}
            required={field.required}
            aria-invalid={missed || undefined}
          />
        )}
        {missed && (
          <div className="ps-1" role="alert">
            <span className={MISSED_LINE}>{missedLine}</span>
          </div>
        )}
        {refusedLine && (
          <div className="mt-1 text-base" role="alert">
            <span className="text-red-700 font-semibold">{refusedLine}</span>
          </div>
        )}
      </div>
    </div>
  );
});
